import { AxiosError } from 'axios';
import { classes } from 'html-classes';
import type { InputMask } from 'imask';
import { observer } from 'mobx-react-lite';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IMaskInput } from 'react-imask';
import { Link, useNavigate } from 'react-router-dom';

import { KYCStatus } from '~/api/KYCStatus';
import { company } from '~/company/Company';
import Modal from '~/components/Modal/Modal';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { useTurnstileWidget } from '~/hooks/useTurnstileWidget';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';

import { mainStore, userStore } from '~/stores';

import Icon from '../Icon/Icon';
import InputPhone from '../InputPhone/InputPhone';
import InputText from '../InputText/InputText';

import ApplySMSButton from './ApplySMSButton/ApplySMSButton';
import { ResponseErrorCodes } from './constants';
import type { OtpErrorBody } from './interface';

const termsUrls = [
  company.config.links.legals?.generalTradingPolicy || { link: '' },
  company.config.links.legals?.privacyPolicy || { link: '' },
];

// https://jiffygrocery.atlassian.net/browse/JS-2419
const PHONE_MAX_LENGTH = 15 + 1;

export default observer(() => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const [phoneVal, setPhoneVal] = useState(userStore.personalData.phone || '');
  const [phoneValid, setPhoneValid] = useState(false);
  const [policyValid, setPolicyValid] = useState<Nullable<boolean>>(null);
  const [codeVal, setCodeVal] = useState('');
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [isPendingCode, setIsPendingCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setCanRequestAgain] = useState(false);
  const [maxAttemptsThreshold, setMaxAttemptsThreshold] = useState(0);
  const [isDeletedUser, setIsDeletedUser] = useState(false);
  const phoneNumber = useRef<string>('');
  const captchaWidgetIdRef = useRef<string | undefined>(undefined);
  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [policyAgreement, setPolicyAgreement] = useState(false);
  const { isMobile } = useGlobal();

  const isCaptchaEnabled = company.isEnabledCaptcha && !mainStore.isRN;

  useTurnstileWidget({
    containerRef: captchaContainerRef,
    widgetIdRef: captchaWidgetIdRef,
    enabled: isCaptchaEnabled,
    siteKey: mainStore.captchaSiteID,
    onSuccess: () => requestCode(),
  });

  useEffect(() => {
    if (!userStore.isAuthorized) {
      return;
    }
    if (
      userStore.isAuthorized &&
      userStore.personalData.kycStatus !== KYCStatus.Verified
    ) {
      navigate('/cabinet/personal-data');
    }
    closeModal();
  }, [userStore.isAuthorized]);

  useEffect(() => {
    analyticsService.track(AnalyticsEvent.LOGIN_SCREEN_SHOWN);
    analyticsService.track(AnalyticsEvent.LOGIN_SCREEN_SHOWN_BI);
  }, []);

  const backHandler = () => {
    closeModal();
  };

  const handleChangePhone = () => {
    setIsPendingCode(false);
    if (captchaWidgetIdRef.current) {
      window.turnstile?.reset(captchaWidgetIdRef.current);
    }
  };

  const validateForm = () => {
    let isValid = true;
    if (!phoneValid) {
      mainStore.pushAlert('error', t('phrases:enterValidPhoneNumber'));
      isValid = false;
    }
    if (!policyAgreement) {
      setPolicyValid(false);
      mainStore.pushAlert(
        'error',
        t('logInTickCheckbox'),
        () => void 0,
        'policy',
      );
      isValid = false;
    }
    return isValid;
  };

  const requestCode = async () => {
    setIsLoading(true);
    try {
      const captchaToken = isCaptchaEnabled
        ? (captchaWidgetIdRef.current &&
            window.turnstile?.getResponse(captchaWidgetIdRef.current)) ||
          ''
        : company.envVariables.REACT_APP_PREDEFINED_CAPTCHA_TOKEN;
      const res = await userStore.requestAuthCode(
        phoneNumber.current,
        captchaToken,
      );
      if ('isDeleted' in res && res.isDeleted) {
        setIsDeletedUser(true);
      }
      setIsPendingCode(true);
      setCanRequestAgain(false);
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.status === 429 &&
        !error.response?.data?.message
      ) {
        mainStore.pushAlert(
          'error',
          `<div>${t('errors:unknown')}<br>${t('phrases:supportLink')}</div>`,
        );
      }
    }
    setIsLoading(false);
  };

  const handlePhoneChange = (val: string) => {
    setPhoneVal(val);
    phoneNumber.current = val;
  };

  const handlePhoneValidate = (flag: boolean) => {
    setPhoneValid(flag);

    if (!flag) {
      setCodeVal('');
      setIsCodeValid(false);
      setIsPendingCode(false);
      setMaxAttemptsThreshold(0);
    } else {
      analyticsService.track(AnalyticsEvent.LOGIN_PHONE_ENTERED, {});
    }
  };

  const handleCodeChange = (val: string, mask: InputMask<any>) => {
    setCodeVal(val);
    setIsCodeValid(mask.masked.isComplete);
  };

  const handleSubmitNumber = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    //!phoneValid || maxAttemptsThreshold > 0 || !policyAgreement
    const widgetId = captchaWidgetIdRef.current;
    if (widgetId && window.turnstile) {
      window.turnstile.reset(widgetId);
      window.turnstile.execute(widgetId);
    } else {
      requestCode();
    }
  };

  const handleSubmitCode = async (
    e: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (!phoneVal || !codeVal) {
      return;
    }
    setIsLoading(true);
    try {
      const data = await userStore.verifyAuthCode(
        phoneVal.replace('+', ''),
        codeVal,
        Boolean(isDeletedUser),
      );
      if ('customer' in data && !data.customer.email && data.previousEmail) {
        mainStore.pushAlert('error', t('errors:emailIsAlreadyUse'));
      }
    } catch (error) {
      const errorBody: OtpErrorBody =
        (error as AxiosError<OtpErrorBody>)?.response?.data ||
        ({} as OtpErrorBody);
      if (errorBody.code) {
        switch (errorBody.code) {
          case ResponseErrorCodes.InvalidOtp:
            mainStore.pushAlert('error', t('errors:invalidCodeError'));
            break;
          case ResponseErrorCodes.TryOtpLimitIsReached:
            mainStore.pushAlert(
              'error',
              t('errors:tryOtpLimitIsReached', {
                timer: errorBody.timeout ? ` in ${errorBody.timeout}s` : '',
              }),
            );
            setMaxAttemptsThreshold(errorBody.timeout ?? 0);
            setIsPendingCode(false);
            setCodeVal('');
            break;
          default:
            mainStore.pushAlert('error', t('errors:otpCodeError'));
        }
      } else {
        mainStore.pushAlert('error', t('errors:otpCodeError'));
      }
      error && console.error(error);
    }
    setIsLoading(false);
  };

  return (
    <Modal isShow className="modal-auth__body">
      <div className="modal-auth" data-company={company.name}>
        <button
          data-button="back"
          className="button _no-padding _no-color input-form__back"
          onClick={backHandler}
        >
          <Icon
            type={isMobile ? 'arrow' : 'close'}
            className={isMobile ? 'icon__rotate-180' : ''}
            size={isMobile ? 24 : 30}
          />
        </button>
        <form onSubmit={isPendingCode ? handleSubmitCode : handleSubmitNumber}>
          {!isPendingCode ? (
            <>
              <h1 className="modal-auth__title">
                {t('phrases:loginOrRegister')}
              </h1>
              <div className="modal-auth__text">
                {t('phrases:enterPhoneNumber')}
              </div>
              <InputPhone
                attributeName="auth-number"
                className={classes([
                  'modal-auth__input-phone',
                  { invalid: !!phoneVal && !phoneValid },
                ])}
                label=""
                value={phoneVal}
                onChange={handlePhoneChange}
                onValidate={handlePhoneValidate}
                autoFocus={true}
                maxLength={PHONE_MAX_LENGTH}
              />
              <div className="modal-auth__agree">
                <label
                  className={classes([
                    'type control__checkbox',
                    policyValid === false && 'error',
                  ])}
                >
                  <input
                    data-checkbox="auth-agree"
                    type="checkbox"
                    checked={policyAgreement}
                    className={'type control__checkbox-input'}
                    onChange={() => {
                      if (!policyAgreement) {
                        setPolicyValid(true);
                      }
                      setPolicyAgreement(!policyAgreement);
                    }}
                  />
                  <span className="type control__checkbox-mark" />
                  <span>
                    <Trans
                      i18nKey="phrases:agreeToCollection"
                      components={termsUrls.map(({ link = '' }) => (
                        <Link to={link} key={link} />
                      ))}
                    />
                  </span>
                </label>
              </div>
              <ApplySMSButton
                isLoading={isLoading}
                isDisabled={!phoneValid || maxAttemptsThreshold > 0}
              >
                {t('verifyNumber')}
              </ApplySMSButton>

              {/*{maxAttemptsThreshold > 0 && (
              <div className="mt-16 fs-14 ">
                <SmsRequestTimer onTimerEnd={() => setMaxAttemptsThreshold(
                  0)} timeoutThreshold={maxAttemptsThreshold} />
              </div>
            )}*/}
            </>
          ) : (
            <>
              <h1 className="modal-auth__title">
                {t('phrases:verifyPhoneNumber')}
              </h1>
              <div className="modal-auth__text">
                {t('phrases:enterCode', { count: 4 })}
              </div>
              <div className="modal-auth__phone phoneNumber">
                {phoneNumber.current}
              </div>
              <InputText
                attributeName="auth-code"
                className="modal-auth__input-code"
                label=""
                value={codeVal}
              >
                <IMaskInput
                  data-input="auth-code-mask"
                  className="input-text"
                  mask="0000"
                  value={codeVal}
                  unmask={true}
                  type="tel"
                  autoFocus={true}
                  onAccept={handleCodeChange}
                  autoComplete="one-time-code"
                />
              </InputText>
              <div className="modal-auth__buttons">
                <div
                  data-button="auth-change-phone"
                  className="button _bordered _block _med"
                  onClick={handleChangePhone}
                >
                  <Icon type="arrow" className="_mirror-x" />
                  {t('changeNumber')}
                </div>
                <ApplySMSButton
                  isLoading={isLoading}
                  isDisabled={!phoneValid || !isCodeValid}
                >
                  {/*{!canRequestAgain ? (
                    <SmsRequestTimer onTimerEnd={() => setCanRequestAgain(true)} />
                  ) : (
                    'Send code'
                  )}*/}
                  {isDeletedUser
                    ? t('enterOTPAndRecoverAccount')
                    : t('enterOTP')}
                </ApplySMSButton>
              </div>
            </>
          )}

          {isCaptchaEnabled && <div ref={captchaContainerRef} />}
        </form>
      </div>
    </Modal>
  );
});
